/** @fileoverview Defines the app schema accepted by the Fabrik backend. */

export type FabrikAppSummary = {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description?: string | null;
  production_app_version_id?: string | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
  /** Name of the last person who edited the app; null when it could not be read. */
  updatedByName?: string | null;
  /** Stable external avatar URL or a freshly signed Core Services S3 URL for the last editor. */
  updatedByProfilePicture?: string | null;
  /** Card details the app list sends in place of the full draft. */
  systemIds?: string[];
  theme?: { logo?: FabrikAppLogo; primaryColor?: string };
  /** Resolved look of an app with a custom logo; null for every other app. */
  appearance?: FabrikResolvedAppearance | null;
  draft_definition?: FabrikAppDefinition;
  publishedVersion?: {
    versionNo: number;
    publishedAt: string;
    definition?: FabrikAppDefinition;
    systemMappings?: Record<string, string>;
  } | null;
  hasUnpublishedChanges?: boolean;
};

export type FabrikAppList = {
  apps: FabrikAppSummary[];
  totalCount: number;
};

export type FabrikLogoPreset = 'northstar' | 'pulse' | 'beacon';
export type FabrikLogoIcon = import('lucide-react/dynamic').IconName;
export type FabrikLogoStyle =
  | 'default'
  | 'rounded'
  | 'outline'
  | 'filled'
  | 'filled-circle'
  | 'circle'
  | 'square'
  | 'serif'
  | 'mono';
export type FabrikAppLogo =
  | { type: 'typography'; color?: string; style?: FabrikLogoStyle }
  | { type: 'icon'; color?: string; icon: FabrikLogoIcon }
  | { type: 'custom'; color?: string; key: string };
export type FabrikAppearanceLogo =
  | { type: 'preset'; preset: FabrikLogoPreset }
  | { type: 'icon'; icon: FabrikLogoIcon }
  | { type: 'image'; url: string };
export type FabrikResolvedAppearance = {
  displayName: string;
  primaryColor: string;
  logo: FabrikAppearanceLogo | null;
};
export type FabrikAppDefinition = {
  schemaVersion: 3;
  app: { name: string; description?: string };
  /** `size` sets text, controls and spaces of the whole app; md (standard) when left out. */
  theme?: {
    primaryColor?: string;
    size?: FabrikAppSize;
    logo?: FabrikAppLogo;
  };
  /** Ordered screen ids for app-shell navigation; only standard screens appear as tabs. */
  navigation?: { screens: string[] };
  screens: FabrikScreen[];
  actions?: FabrikAction[];
  /** PDF documents, defined once and named by id from export steps. */
  documents?: FabrikPdfDocument[];
  /** Values the builder types once for the whole app; the same value is used on every row. */
  variables?: FabrikVariable[];
  /** Named email lists; an action names the groups whose members may run it. */
  userGroups?: FabrikUserGroup[];
};

/** A named list of emails. A hosted app gets only the id and label, never the emails. */
export type FabrikUserGroup = { id: string; label: string; emails?: string[] };

export type FabrikInputValue = string | number | boolean | Array<string | number | boolean>;
export type InputSnapshot = {
  standalone?: Record<string, FabrikInputValue>;
  forms?: Record<string, Record<string, FabrikInputValue>>;
};

/** A value the builder types once for the whole app, named and typed so it can be used as a ref. */
export type FabrikVariable = {
  id: string;
  label: string;
  type: 'email' | 'text' | 'date';
  value: string;
};

/** A reference to a column or navigation path of the record the screen shows (the row that opened it). */
export type FabrikRecordRefValue = {
  type: 'ref';
  scope: 'record';
  dataSourceId: string;
  field: string;
};

/** A reference to one app variable's value; `field` is the variable id. */
export type FabrikVariableRefValue = {
  type: 'ref';
  scope: 'variables';
  field: string;
};

/**
 * A reference to one checklist item in scope, such as `owner.email` in a send step's `to`.
 * The UI never resolves it, since no item is ever in scope while rendering.
 */
export type FabrikItemRefValue = {
  type: 'ref';
  scope: 'item';
  componentId: string;
  field: string;
};

/**
 * Fields of the read-only globals: the current date and time, the lowest and highest dates
 * (`YYYY-MM-DD`, for open-ended effective date ranges), and the signed-in viewer.
 */
export type FabrikGlobals = {
  currentDate: string;
  currentDateSap: string;
  currentDateTime: string;
  currentDateTimeSap: string;
  lowDate: string;
  highDate: string;
  currentUserFirstName: string;
  currentUserLastName: string;
  currentUserEmail: string;
};

export type FabrikGlobalField = keyof FabrikGlobals;

/** A reference to one globals field, such as today's date or the viewer's email. */
export type FabrikGlobalsRefValue = {
  type: 'ref';
  scope: 'globals';
  field: FabrikGlobalField;
};

/** A captured standalone input or an input captured by one named form. */
export type FabrikInputRefValue = {
  type: 'ref';
  scope: 'inputs';
  field: string;
  formId?: string;
};

/** A reference to a column of each row of the same list; valid only in list columns, computed inputs, and joins. */
export type FabrikDataSourceRefValue = {
  type: 'ref';
  scope: 'dataSource';
  dataSourceId: string;
  field: string;
};

export type FabrikInputInitialRefValue =
  | FabrikRecordRefValue
  | FabrikVariableRefValue
  | FabrikGlobalsRefValue
  | FabrikInputRefValue;

export type FabrikValueRefValue =
  | FabrikRecordRefValue
  | FabrikVariableRefValue
  | FabrikItemRefValue
  | FabrikGlobalsRefValue
  | FabrikInputRefValue;

/** All refs, including dataSource refs reserved for list rows and computed columns. */
export type FabrikRefValue = FabrikValueRefValue | FabrikDataSourceRefValue;

/** Refs a list reads per row: its own row's columns, or a globals field. */
export type FabrikListRefValue = FabrikDataSourceRefValue | FabrikGlobalsRefValue;

/** Refs that can identify a checklist or timeline record, or fill an OData row filter. */
export type FabrikRecordKeyRefValue =
  | FabrikRecordRefValue
  | FabrikGlobalsRefValue
  | FabrikInputRefValue;

/**
 * How a date value is written as text, in UTC: `day` 2026-10-09, `dayTime` 2026-10-09 1430,
 * `month` 2026-10, `year` 2026. Used for file names, where a readable date is not wanted.
 */
export type FabrikDateFormat = 'day' | 'dayTime' | 'month' | 'year';

/** A date moved by a whole number of days from another date-giving value. */
export type FabrikDateValue = {
  type: 'date';
  from: string | FabrikValueRefValue;
  offsetDays?: number;
  /** When set, the value is this fixed text form instead of a readable date. */
  format?: FabrikDateFormat;
};

/**
 * A message built from fixed text with named slots filled in by other values. Valid only as a
 * `send` step's `text` or `subject`, inside another template's `values`, or as a `list.item`.
 * Resolved and rendered on the server; the UI never renders a template itself.
 */
export type FabrikTemplateValue = {
  type: 'template';
  template: string;
  values: Record<string, FabrikValue>;
};

/** Values a `list.item` may hold; a list cannot hold another list. */
export type FabrikListItemValue =
  | string
  | FabrikValueRefValue
  | FabrikDateValue
  | FabrikTemplateValue;

/**
 * Renders `item` once per row of a `send` step and joins the results with `separator`
 * (default `", "`). Valid only inside a template's `values`.
 */
export type FabrikListValue = {
  type: 'list';
  item: FabrikListItemValue;
  separator?: string;
};

/** Anything the app reads at run time: a fixed value, or a reference, date, template, or list built from one. */
export type FabrikValue =
  | string
  | FabrikValueRefValue
  | FabrikDateValue
  | FabrikTemplateValue
  | FabrikListValue;

/** A link address with blanks; the server fills it when the link opens. */
export type FabrikLinkUrlTemplate = {
  type: 'template';
  template: string;
  values: Record<string, unknown>;
};

export type FabrikTarget =
  | { type: 'link'; url: string | FabrikLinkUrlTemplate }
  | { type: 'screen'; screenId: string };

/** Button target that runs an entry of the definition's `actions`. */
export type FabrikActionTarget = { type: 'action'; actionId: string };

export type FabrikButtonTarget = FabrikTarget | FabrikActionTarget;

/** Size presets of a whole app: compact, standard or roomy. */
export const FABRIK_APP_SIZES = ['sm', 'md', 'lg'] as const;
export type FabrikAppSize = (typeof FABRIK_APP_SIZES)[number];
/** Moves one component a step smaller or larger than its usual look; md is the usual look. */
export const FABRIK_COMPONENT_SIZES = ['sm', 'md', 'lg', 'xl'] as const;
export type FabrikComponentSize = (typeof FABRIK_COMPONENT_SIZES)[number];
/** Side panel widths; md is the usual width. */
export const FABRIK_PANEL_SIZES = ['md', 'lg', 'xl'] as const;
/** Button looks: ghost has no border, danger marks an action that removes or ends something. */
export const FABRIK_BUTTON_VARIANTS = ['primary', 'secondary', 'ghost', 'danger'] as const;
export type FabrikButtonStyle = { variant: (typeof FABRIK_BUTTON_VARIANTS)[number] };

export type FabrikScreen = {
  id: string;
  kind: 'standard' | 'detail';
  label: string;
  displayType?: 'sidebar' | 'page';
  /** Side panel screens only: the panel width, md (the usual width) when left out. */
  size?: (typeof FABRIK_PANEL_SIZES)[number];
  /** Detail screens only: the record's name and a short line under it. */
  header?: FabrikScreenHeader;
  components: FabrikComponent[];
};

/** Title and subtitle of a detail screen; each is a list of values joined on screen. */
export type FabrikScreenHeader = {
  /** Joined with one space, so a first and last name read as one name. */
  title: FabrikValue[];
  /** Joined with " · ". */
  subtitle?: FabrikValue[];
};

export type FabrikTextComponent = {
  id: string;
  kind: 'text';
  style: { variant: 'heading' | 'subheading' | 'body' | 'note' | 'eyebrow' | 'display' };
  text: FabrikValue;
  size?: FabrikComponentSize;
};

export type FabrikIconComponent = {
  id: string;
  kind: 'icon';
  name: string;
  /** A size step, or a fixed size in pixels as older apps set it. */
  size?: FabrikComponentSize | number;
  label?: string;
};

export type FabrikMetricComponent = {
  id: string;
  kind: 'metric';
  label: string;
  supportingText?: string;
  dataSource: FabrikListDataSource;
  size?: FabrikComponentSize;
};

export type FabrikButtonComponent = {
  id: string;
  kind: 'button';
  style: FabrikButtonStyle;
  label: FabrikValue;
  target: FabrikButtonTarget;
  /** Shows the button only when the named checklist or timeline has no rows, or any rows. */
  showWhen?: { componentId: string; rows: 'none' | 'any' };
  size?: FabrikComponentSize;
};

export type FabrikListItem = {
  id: string;
  values: Record<string, string | number | boolean>;
};

export type FabrikItemsDataSource = {
  type: 'items';
  id: string;
  items: FabrikListItem[];
};

export type FabrikRowField = { dataSourceId: string; field: string };
export type FabrikRowValues = Record<string, Record<string, unknown> | null>;

export type FabrikOrderBy = { field: string; direction?: 'asc' | 'desc' };
export type FabrikODataFilterOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains';
export type FabrikODataFieldType = 'Edm.String' | 'Edm.DateTime' | 'Edm.DateTimeOffset';

/** Keeps rows whose `field` compares to a record, global, or committed input value; `operator` defaults to `eq`. */
export type FabrikODataRowFilter = {
  field: string;
  value: FabrikRecordKeyRefValue;
  operator?: FabrikODataFilterOperator;
  fieldType?: FabrikODataFieldType;
  /** Use an input as an optional query value; omit the filter when it is blank. */
  omitWhenEmpty?: boolean;
  /** Apply only when another input matches the configured value. */
  when?: { value: FabrikInputRefValue; equals: string | number | boolean };
};

export type FabrikOptionItem = {
  label: string;
  value: string | number | boolean;
  /** For a legacy picklist value: the option id a record read gives, which picks this option too. */
  optionId?: string;
};
/** A field of a system entity whose values (picklist values or foundation object records) are the options. */
export type FabrikSystemField = { systemId: string; entity: string; field: string };
export type FabrikInputOptions =
  | { items: FabrikOptionItem[] }
  | {
      odata: FabrikODataOptionsDataSource;
      label: FabrikDataSourceRefValue;
      value: FabrikDataSourceRefValue;
    }
  | { systemField: FabrikSystemField };

/** One value a system field can take; `optionId` is set for legacy picklists, whose reads can give it. */
export type FabrikFieldOption = { value: string; label: string; optionId?: string };
/**
 * How the browser gets the options of one system field: the whole list (picklists), a search
 * page by page (foundation objects in the entity cache), or none, so the viewer types the code.
 */
export type FabrikFieldOptionsAnswer =
  | { kind: 'list'; options: FabrikFieldOption[] }
  | { kind: 'search' }
  | { kind: 'text' };
/** Field options by field name, for the fields of one entity that have them. */
export type FabrikFieldOptions = Record<string, FabrikFieldOptionsAnswer>;
/** Asks for a search page of one system field's values, or for the labels of some codes. */
export type FabrikFieldValuesRequest = FabrikSystemField & {
  search?: string;
  top?: number;
  skip?: number;
  values?: string[];
};
/** One page of a system field's values, from a search or a lookup of some codes. */
export type FabrikFieldValuesPage = { results: FabrikFieldOption[]; hasMore: boolean };
export type FabrikODataOptionsDataSource = Omit<FabrikODataDataSource, 'values'> & {
  fields?: string[];
  limit?: number;
};
export type FabrikInputType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'tel'
  | 'url'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'switch'
  | 'lookup';
export type FabrikInputConstraints =
  | { minLength?: number; maxLength?: number; pattern?: string }
  | { min?: number; max?: number; step?: number }
  | { min?: string; max?: string; step?: number };
export type FabrikInputComponent = {
  id: string;
  kind: 'input';
  inputType: FabrikInputType;
  label: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  initialValue?:
    | string
    | number
    | boolean
    | Array<string | number | boolean>
    | FabrikInputInitialRefValue;
  constraints?: FabrikInputConstraints;
  valueType?: 'string' | 'number' | 'boolean';
  options?: FabrikInputOptions;
  presentation?: 'default' | 'segmented';
  /** On a form without a layout, takes a full row; otherwise the input takes half a row. */
  fullRow?: boolean;
};
/** Sizing of an item in a flex grid: its share of the free space and its starting width. */
export type FabrikFlexItemSize = {
  grow?: number;
  basis?: 'auto' | 'sm' | 'md' | 'lg' | 'xl';
};
export type FabrikFormLayoutItem =
  | ({
      type: 'input';
      inputId: string;
      span?: number;
      align?: 'start' | 'center' | 'end' | 'stretch';
    } & FabrikFlexItemSize)
  | ({
      type: 'submit';
      span?: number;
      align?: 'start' | 'center' | 'end' | 'stretch';
    } & FabrikFlexItemSize);
export type FabrikFormLayout = {
  kind: 'grid';
  /** A column grid (the default) uses columns and spans; a flex grid wraps items by grow and basis. */
  mode?: 'columns' | 'flex';
  columns?: number;
  gap: 'sm' | 'md' | 'lg';
  items: FabrikFormLayoutItem[];
};
export type FabrikFormComponent = {
  id: string;
  kind: 'form';
  title?: string;
  inputs: FabrikInputComponent[];
  /** Optional form-only grid. Items arrange existing fields and the form submit button. */
  layout?: FabrikFormLayout;
  /** Titled groups of the inputs, by id; inputs in no section show first. */
  sections?: { title: string; note?: string; inputIds: string[] }[];
  submit: {
    label: string;
    style: FabrikButtonStyle;
    /** A submitted form opens a screen or runs an action. */
    target?: { type: 'screen'; screenId: string } | { type: 'action'; actionId: string };
  };
};

/** One day of an effective dated read: a fixed `YYYY-MM-DD` day, or a ref like a row filter value. */
export type FabrikEffectiveDay = string | FabrikRecordKeyRefValue;

/**
 * Which effective dated records an OData read returns: the ones valid on one day (`asOf`), or
 * the ones valid in a range (`from` and `to`, at least one of them).
 */
export type FabrikEffectiveDates =
  | { asOf: FabrikEffectiveDay }
  | { from?: FabrikEffectiveDay; to?: FabrikEffectiveDay };

export type FabrikODataDataSource = {
  type: 'odata';
  id: string;
  systemId: string;
  entity: string;
  values?: string[];
  expand?: string[];
  filter?: string;
  orderBy?: FabrikOrderBy[];
  rowFilters?: FabrikODataRowFilter[];
  effectiveDates?: FabrikEffectiveDates;
};

/** A value a Workday read sends: fixed text, or a ref like a row filter value. */
export type FabrikWorkdayValue = string | FabrikRecordKeyRefValue;

/** One ID of a Workday record: its ID type and value, such as `Employee_ID` and `21001`. */
export type FabrikWorkdayIdPair = { type: string; value: FabrikWorkdayValue };

/**
 * Records of one Workday Get operation, such as `Human_Resources.Get_Workers`. Rows are flat and
 * keyed by the dotted paths in `values`; a link holds its display name and `<path>.<ID type>`
 * holds each of its IDs. Workday reads have no filter and no sort.
 */
export type FabrikWorkdayDataSource = {
  type: 'workday';
  id: string;
  systemId: string;
  entity: string;
  values: string[];
  references?: FabrikWorkdayIdPair[];
  criteria?: Record<string, boolean | FabrikWorkdayValue | FabrikWorkdayIdPair>;
  asOf?: FabrikEffectiveDay;
  responseGroup?: Record<string, boolean>;
};

/** A data source read from a connected system. */
export type FabrikSystemDataSource = FabrikODataDataSource | FabrikWorkdayDataSource;

export type FabrikListDataSource =
  | FabrikItemsDataSource
  | FabrikODataDataSource
  | FabrikWorkdayDataSource;
export type FabrikListRuntimeRow = Record<string, unknown>;
export type FabrikRenderedListRow = { values: FabrikRowValues; key?: string };
export type FabrikListQueryOptions = { top: number; skip: number } | { count: true };

/** A list column that shows one value of each row as it is. */
export type FabrikFieldColumn = {
  kind?: 'field';
  name: string;
  value: FabrikDataSourceRefValue;
  label: string;
  type: 'text' | 'number' | 'date' | 'url';
};

/** Names of the calculations a computed list column can run. */
export type FabrikColumnComputeName = 'daysUntil' | 'daysSince' | 'yearsSince' | 'join';

/** One computed-column input: a ref, a list of refs, or a plain text option. */
export type FabrikComputedInput = FabrikListRefValue | FabrikListRefValue[] | string;

/** A list column whose value is calculated from other row fields. */
export type FabrikComputedColumn = {
  kind: 'computed';
  name: string;
  compute: FabrikColumnComputeName;
  /** Maps each input to a ref, a list of refs, or (for `separator`) text. */
  inputs: Record<string, FabrikComputedInput>;
  label: string;
};

export type FabrikPersonColumn = {
  kind: 'person';
  name: string;
  label: string;
  primary: FabrikDataSourceRefValue;
  secondary?: FabrikDataSourceRefValue;
};

export type FabrikStatusColumn = {
  kind: 'status';
  name: string;
  label: string;
  value: FabrikDataSourceRefValue;
  options: {
    value: string | number | boolean;
    label: string;
    tone: 'neutral' | 'positive' | 'warning' | 'negative';
  }[];
};

export type FabrikListColumn =
  | FabrikFieldColumn
  | FabrikComputedColumn
  | FabrikPersonColumn
  | FabrikStatusColumn;

export type FabrikListSortChoice = { id: string; label: string; orderBy: FabrikOrderBy[] };
export type FabrikListFilterOption = { label: string; value: string | number | boolean };
export type FabrikListFilter = {
  id: string;
  label: string;
  field: string;
  options: FabrikListFilterOption[];
  fieldType?: FabrikODataFieldType;
};

/** Looks up `dataSourceId` rows whose `field` equals the `on` value of each list row. */
export type FabrikListJoin = {
  dataSourceId: string;
  field: string;
  on: FabrikListRefValue;
  fieldType?: FabrikODataFieldType;
};

export type FabrikListComponent = {
  id: string;
  kind: 'list';
  title?: string;
  columns: FabrikListColumn[];
  /** Rows per page (1–100, default 50); also caps static lists. */
  limit?: number;
  dataSource: FabrikListDataSource[];
  primaryDataSourceId: string;
  joins?: FabrikListJoin[];
  target?: FabrikTarget;
  /** Buttons in the list's header bar, right of the title; they run like a button on the screen. */
  headerActions?: FabrikListHeaderAction[];
  resultCount?: boolean;
  sortChoices?: FabrikListSortChoice[];
  filters?: FabrikListFilter[];
};

/** Kinds of components whose rows are saved per record. */
export type FabrikStateKind = 'checklist' | 'timeline';

/** A checklist or timeline block; its rows are saved per record. */
export type FabrikStateComponent<K extends FabrikStateKind = FabrikStateKind> = {
  id: string;
  kind: K;
  title?: string;
  /** Record or globals refs that identify which record the rows belong to. */
  recordKey: FabrikRecordKeyRefValue[];
};

/** A labelled button that runs one action. */
export type FabrikActionEntry = {
  label: string;
  target: FabrikActionTarget;
};

/** One entry of a checklist row's row button; runs an action for that row. Checklist only, max 3. */
export type FabrikChecklistItemAction = FabrikActionEntry;

/** One button in a list's header bar (1 to 3 per list). */
export type FabrikListHeaderAction = FabrikActionEntry & {
  /** Primary uses the theme color; secondary (default) is plain. */
  style?: FabrikButtonStyle;
};

export type FabrikChecklistComponent = FabrikStateComponent<'checklist'> & {
  itemActions?: FabrikChecklistItemAction[];
};

export type FabrikTimelineComponent = FabrikStateComponent<'timeline'>;

/** One label and value pair of a fields block. */
export type FabrikFieldsItem = {
  label: string;
  value: FabrikValue;
};

/** A grid of labels, each over its value. */
export type FabrikFieldsComponent = {
  id: string;
  kind: 'fields';
  items: FabrikFieldsItem[];
};

/** Chart forms: bars (stacked when split), a donut, or a line over time. */
export type FabrikChartType = 'bar' | 'donut' | 'line';

/** Date steps of a line chart; month when left out. */
export type FabrikChartInterval = 'day' | 'week' | 'month' | 'quarter' | 'year';

/** What a chart adds up per group: the row count, or the sum or average of a number column. */
export type FabrikChartMeasure = { fn: 'count' } | { fn: 'sum' | 'average'; column: string };

/** A chart of the rows of a list on the same screen; the server reads and groups the rows. */
export type FabrikChartComponent = {
  id: string;
  kind: 'chart';
  title?: string;
  type: FabrikChartType;
  /** Id of a list component on the same screen; the chart follows its filters. */
  from: string;
  /** Column name of that list that makes the categories (a date column for a line chart). */
  groupBy: string;
  /** Line only. */
  interval?: FabrikChartInterval;
  /** Column name that splits each category into series. Not for donut. */
  splitBy?: string;
  /** Count when left out. */
  measure?: FabrikChartMeasure;
  size?: FabrikComponentSize;
};

/** Components that can sit inside a layout container or directly on a screen. */
export type FabrikLeafComponent =
  | FabrikTextComponent
  | FabrikIconComponent
  | FabrikMetricComponent
  | FabrikButtonComponent
  | FabrikListComponent
  | FabrikChecklistComponent
  | FabrikTimelineComponent
  | FabrikFieldsComponent
  | FabrikChartComponent
  | FabrikInputComponent;

export type FabrikGridItem = {
  component: FabrikComponent;
  span?: number;
  align?: 'start' | 'center' | 'end' | 'stretch';
} & FabrikFlexItemSize;

export type FabrikGridComponent = {
  id: string;
  kind: 'grid';
  /** A column grid (the default) uses columns and spans; a flex grid wraps items by grow and basis. */
  mode?: 'columns' | 'flex';
  columns?: number;
  gap: 'sm' | 'md' | 'lg';
  items: FabrikGridItem[];
};

export type FabrikCardComponent = {
  id: string;
  kind: 'card';
  title?: string;
  note?: string;
  content: FabrikComponent;
};

export type FabrikComponent =
  | FabrikLeafComponent
  | FabrikFormComponent
  | FabrikGridComponent
  | FabrikCardComponent;

/** Who owns a checklist row: an email (required) and an optional display name. */
export type FabrikOwner = { name?: FabrikValue; email: FabrikValue };

/** The owner of a stored checklist row. Rows saved before names existed hold a plain email string. */
export type StoredOwner = string | { name?: string; email: string };

/** Keeps rows in a checklist or timeline on the same screen. */
export type FabrikStoreStep = {
  kind: 'store';
  target: { componentId: string };
  /** Checklist rows are `{ label, owner?, dueDate? }`, timeline rows are `{ text }`. */
  rows: ({ label: string; owner?: FabrikOwner; dueDate?: FabrikValue } | { text: string })[];
};

/**
 * An email recipient. `address` must give an email: an email string, a record/variables ref,
 * or (with `items`) an item ref to `owner.email`.
 */
export type FabrikEmailTo = { type: 'email'; address: FabrikValue };

export type FabrikSendStep = {
  kind: 'send';
  /** The server still reads an older bare value here and turns it into this shape. */
  to: FabrikEmailTo;
  /** Rows to send over: a checklist's open or overdue rows on the same screen as the action's button. */
  items?: { componentId: string; filter: 'open' | 'overdue' };
  /** One message per distinct resolved `to` address; needs `items`. */
  groupBy?: 'to';
  /** Defaults to the app name when left out. */
  subject?: string | FabrikTemplateValue;
  text: FabrikTemplateValue;
};

/** A file name: fixed text or a template, such as one with a date value in `day` format. */
export type FabrikFileName = string | FabrikTemplateValue;

/** Where an export step puts its file. */
export type FabrikExportTo =
  | { type: 'download'; fileName: FabrikFileName }
  | {
      type: 'email';
      address: FabrikValue;
      /** Defaults to the file name without its extension. */
      subject?: string | FabrikTemplateValue;
      fileName: FabrikFileName;
    }
  | {
      type: 'sharepoint';
      /** Id of the SharePoint system that receives the file. */
      systemId: string;
      /** Folder path in the SharePoint site. Plain text, may be empty. */
      folder: string;
      fileName: FabrikFileName;
    };

/** One block of a PDF document; the server draws it, so the UI only carries it. */
export type FabrikPdfBlock =
  | { kind: 'heading'; text: FabrikValue }
  | { kind: 'text'; text: FabrikValue; label?: string; style?: { variant: 'quote' } }
  | {
      kind: 'fields';
      items: { label: string; value: FabrikValue; link?: string | FabrikLinkUrlTemplate }[];
      columns?: 1 | 2;
    }
  | {
      kind: 'table';
      from: { componentId: string };
      label?: string;
      link?: { column: string; url: string | FabrikLinkUrlTemplate };
    }
  | { kind: 'divider' };

/** A PDF document, defined once at the top of the app. */
export type FabrikPdfDocument = {
  id: string;
  label: string;
  page?: { orientation: 'portrait' | 'landscape' };
  blocks: FabrikPdfBlock[];
};

/**
 * Builds an Excel file from the rows of a list on the same screen as the action's button, or
 * draws a shared PDF document from that screen's record, inputs and lists.
 */
export type FabrikExportStep =
  | {
      kind: 'export';
      from: { componentId: string };
      format: 'xlsx';
      to: FabrikExportTo;
    }
  | {
      kind: 'export';
      format: 'pdf';
      /** Id of the shared document in `documents`. */
      document: string;
      to: FabrikExportTo;
    };

/** Writes one record of a connected system. The server refuses to run it until writes are built. */
export type FabrikSystemWriteStep = {
  kind: 'write';
  target: { systemId: string; entity: string };
  intent: 'create' | 'update' | 'createOrUpdate';
  values: { field: string; value: FabrikValue }[];
  onFailure?: 'stop' | 'continue';
};

export type FabrikActionStep =
  | FabrikStoreStep
  | FabrikSystemWriteStep
  | FabrikSendStep
  | FabrikExportStep;

/** A question the viewer answers before an action runs. */
export type FabrikActionConfirm = { title: string; message: string; button: string };

export type FabrikAction = {
  id: string;
  label: string;
  confirm?: FabrikActionConfirm;
  /** Ids of the user groups whose members may run the action; without it everyone may. */
  allowedGroups?: string[];
  /** true: every step must succeed and the system writes save together; false: one step at a time. */
  atomic?: boolean;
  steps: FabrikActionStep[];
};

/** One saved row of a checklist or timeline. */
export type ComponentStateRow = {
  key: string;
  /** For a checklist row, `owner` is a `StoredOwner`. */
  value: Record<string, unknown> & { owner?: StoredOwner };
  updatedAt: string;
};

/** Rows of one checklist or timeline for one record. */
export type ComponentStateRead = {
  componentId: string;
  kind: FabrikStateKind;
  record: string;
  rows: ComponentStateRow[];
};

/** The filter chips picked in each list of a screen: `index` into the list's filters and its value. */
export type FabrikListFilterChoices = Record<
  string,
  { index: number; value: string | number | boolean }[]
>;

/** Why the server could not draw one chart. */
export type FabrikChartErrorCode = 'TOO_MANY_ROWS' | 'OVER_BUDGET' | 'TIMEOUT' | 'QUERY_FAILED';

/**
 * One chart's numbers. `values[seriesIndex][categoryIndex]` is a number, or null for an average
 * with no values. Keys `__other__`, `__empty__` and `__all__` mark the merged rest, a blank value
 * and the one series of a chart that is not split.
 */
export type FabrikChartData = {
  id: string;
  status: 'ok';
  categories: { key: string; label: string }[];
  series: { key: string; label: string }[];
  values: (number | null)[][];
  rowCount: number;
  truncated: { categories: boolean; series: boolean };
};

/** One entry of a charts read: the numbers, or an error code. */
export type FabrikChartResult =
  | FabrikChartData
  | { id: string; status: 'error'; code: FabrikChartErrorCode };

/** Answer of one charts read for a whole screen. */
export type FabrikChartsRead = {
  charts: FabrikChartResult[];
  timing?: { requests: number; durationMs: number };
};

/** One export an action run sent by email, or saved to SharePoint. */
export type AppActionExport =
  | { type: 'email'; fileName: string }
  | { type: 'sharepoint'; fileName: string; webUrl: string };

/**
 * How one write step to a system ended. `unknown`: the system got the record but did not answer.
 * `not_saved`: another write of the same all-or-nothing save failed, so this one was rolled back.
 */
export type AppActionWriteOutcome =
  | 'saved'
  | 'pending_approval'
  | 'failed'
  | 'unknown'
  | 'skipped'
  | 'not_saved';

/** One write step of an action run, in the order the action lists them. */
export type AppActionWrite = {
  position: number;
  entity: string;
  outcome: AppActionWriteOutcome;
  /** The system's message, or why the step did not run. */
  message?: string;
  /** Set when the record was saved or sent for approval: its key fields and the values sent. */
  record?: { keys: string[]; values: Record<string, unknown> };
};

/**
 * JSON answer of an action run. `sent` is set when the action ran a `send` step, `exports` when
 * it ran an email or SharePoint export, `writes` when it has write steps. `repeated` is true when
 * the run key was used before: the answer is the first run's, and nothing ran again.
 * An action with a download export answers with the file instead.
 */
export type AppActionRunResult = {
  actionId: string;
  written: number;
  sent?: number;
  exports?: AppActionExport[];
  writes?: AppActionWrite[];
  repeated?: boolean;
  /** Set when the writes saved but a later store, send or export step failed. */
  partial?: { kind: 'store' | 'send' | 'export'; message: string };
};
